import { useEffect, useRef, useState } from 'preact/hooks';
import { PaperclipIcon } from './Icons.jsx';
import { formatBytes, isImage } from '../upload.js';

const time = (value) =>
  new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const dayLabel = (value) => {
  const date = new Date(value);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86400000);
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(date, today)) return 'TODAY';
  if (same(date, yesterday)) return 'YESTERDAY';
  return date.toLocaleDateString([], { day: 'numeric', month: 'short' }).toUpperCase();
};

/**
 * One attachment.
 *
 * On a private bucket there is no lasting URL to put in an `href`, so opening
 * is a click that signs a URL and then follows it. Images resolve their
 * preview up front — a screenshot the agent sent is meant to be seen, not
 * downloaded.
 */
function Attachment({ file, onResolve }) {
  const [preview, setPreview] = useState(file.url ?? null);
  const [busy, setBusy] = useState(false);
  const image = isImage(file.mimeType);

  useEffect(() => {
    if (file.expired || !image || preview || !onResolve || !file.key) return undefined;
    let live = true;
    onResolve(file)
      .then((url) => live && setPreview(url))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [file, image, preview, onResolve]);

  const open = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const url = preview ?? (await onResolve(file));
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      /* the link expired or storage is unreachable; the row stays clickable */
    } finally {
      setBusy(false);
    }
  };

  // Storage dropped it after the retention window. The line stays so the
  // thread still reads, but there is nothing left to open.
  if (file.expired) {
    return (
      <div class="attachment attachment--expired">
        <span class="attachment__name">{file.name || 'Attachment'}</span>
        <span class="attachment__size">no longer available</span>
      </div>
    );
  }

  return (
    <div class="attachment">
      {image && preview && (
        <button class="attachment__thumb" type="button" onClick={open} aria-label="Open full size">
          <img src={preview} alt={file.name || 'Attachment'} loading="lazy" />
        </button>
      )}
      <button class="attachment__row" type="button" onClick={open}>
        <PaperclipIcon size={12} />
        <span class="attachment__name">{file.name || 'Attachment'}</span>
        {file.size > 0 && <span class="attachment__size">{formatBytes(file.size)}</span>}
      </button>
    </div>
  );
}

function Attachments({ items, onResolve }) {
  if (!items?.length) return null;
  return items.map((file) => (
    <Attachment key={file.key || file.url} file={file} onResolve={onResolve} />
  ));
}

function Bubble({ message, onRetry, onResolveAttachment }) {
  const mine = message.senderType === 'visitor';

  if (message.senderType === 'system') {
    return <div class="bubble bubble--system">{message.body}</div>;
  }

  /* Withdrawn by the agent. The line stays rather than the bubble vanishing:
     the visitor may have already read it, and a message that silently
     disappears is more unsettling than one that says it was taken back. */
  if (message.deleted) {
    return (
      <div class={`bubble-wrap bubble-wrap--${mine ? 'out' : 'in'}`}>
        <div class={`bubble bubble--${mine ? 'out' : 'in'} bubble--deleted`}>
          This message was deleted
        </div>
        <div class="bubble__meta">{time(message.createdAt)}</div>
      </div>
    );
  }

  /* A message sent as a user alert renders exactly like any other agent
     message. `isEmergency` still arrives on the payload — the dashboard needs
     it, and the store still reads it to force the panel open — but nothing
     here branches on it any more. */

  const meta = mine
    ? message.failed
      ? 'Not sent · tap to retry'
      : message.pending
        ? 'Sending…'
        : `${time(message.createdAt)}${message.readAt ? ' · Read' : ' · Delivered'}`
    : // An agent's correction is marked as one. Quietly swapping the words of
      // something already read is how a transcript stops being trustworthy.
      `${time(message.createdAt)}${message.editedAt ? ' · edited' : ''}`;

  return (
    <div class={`bubble-wrap bubble-wrap--${mine ? 'out' : 'in'}`}>
      <div
        class={`bubble bubble--${mine ? 'out' : 'in'}`}
        style={message.failed ? { opacity: 0.6 } : undefined}
        onClick={message.failed ? () => onRetry(message) : undefined}
      >
        {message.body}
        <Attachments items={message.attachments} onResolve={onResolveAttachment} />
      </div>
      <div class="bubble__meta">{meta}</div>
    </div>
  );
}

export function Thread({
  messages,
  agentTyping,
  hasMore,
  loadingMore,
  onLoadMore,
  onRetry,
  onResolveAttachment,
}) {
  const scroller = useRef(null);
  const pinned = useRef(true);

  // Stay pinned to the newest message, but never yank the view away from
  // someone who has scrolled up to read history.
  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  useEffect(() => {
    const el = scroller.current;
    if (el && pinned.current) el.scrollTop = el.scrollHeight;
  }, [messages.length, agentTyping]);

  let lastDay = null;

  return (
    <div class="thread" ref={scroller} onScroll={onScroll} role="log" aria-live="polite">
      {hasMore && (
        <button class="thread__more" type="button" onClick={onLoadMore} disabled={loadingMore}>
          {loadingMore ? 'Loading…' : 'Load earlier messages'}
        </button>
      )}

      {messages.map((message) => {
        const day = dayLabel(message.createdAt);
        const divider = day !== lastDay ? day : null;
        lastDay = day;

        return (
          <>
            {divider && (
              <div class="thread__day" key={`d-${message.id}`}>
                {divider} · {time(message.createdAt)}
              </div>
            )}
            <Bubble
              key={message.id}
              message={message}
              onRetry={onRetry}
              onResolveAttachment={onResolveAttachment}
            />
          </>
        );
      })}

      {agentTyping && (
        <div class="typing" aria-label="Agent is typing">
          <span />
          <span />
          <span />
        </div>
      )}
    </div>
  );
}

export default Thread;
